import React from 'react';

import {ContextTrackingData} from '../context/tracking/TrackingService';
import {useContextTrackingData} from './useContextTrackingData';
import {processContentId} from '../utils/processContentId';
import {NavUiTrackingProps} from '../utils/tracking/useTrackNavUiClick';
import {TrackSliderLoadArguments} from '../utils/tracking/useTrackSliderLoad';
import {SliderTrackingEventProps} from '../d6/types';

export type GetContentIdArgs = {
    index: number;
    headingLabel: string;
    cqPath: string;
};

export type AutoScrollTrackingProps = {
    id: string;
    contextData: ContextTrackingData;
    isRotating: boolean;
};

export type HandleTrackingEventProps = Readonly<{
    contentLabels: string;
    cqPath: string;
    handleLoadEvent: (_: TrackSliderLoadArguments) => void;
    handleManualEvent: (_: NavUiTrackingProps) => void;
    handleRotatingEvent: (_: AutoScrollTrackingProps) => void;
}>;

export function useHandleTrackingEvents({
    contentLabels,
    cqPath,
    handleLoadEvent,
    handleManualEvent,
    handleRotatingEvent
}: HandleTrackingEventProps) {
    const contextData = useContextTrackingData();

    return React.useCallback(
        ({
            index,
            headingLabel,
            itemCount,
            itemPosition,
            loadType,
            actionName,
            isRotating
        }: SliderTrackingEventProps): void => {
            const contentId = getContentId({
                index,
                headingLabel,
                cqPath
            });

            if (typeof isRotating === 'boolean') {
                handleRotatingEvent({
                    id: contentId,
                    contextData: {...contextData, itemCount, itemPosition},
                    isRotating
                });
            }

            if (loadType) {
                if (actionName && loadType === 'manual') {
                    handleManualEvent({
                        actionName,
                        itemCount,
                        itemPosition,
                        itemId: contentId
                    });
                }
                handleLoadEvent({
                    contentId,
                    contentLabels,
                    itemCount,
                    itemPosition,
                    loadType
                });
            }
        },
        [
            cqPath,
            handleManualEvent,
            handleLoadEvent,
            handleRotatingEvent,
            contentLabels,
            contextData
        ]
    );
}

export const getContentId = ({
    index,
    headingLabel,
    cqPath
}: GetContentIdArgs): string =>
    processContentId(`${cqPath}/item_${index}`, `Slider-${headingLabel}`);
